import clsx from 'clsx';

/**
 * Base shimmer block — sized/shaped entirely via className. The shimmer
 * animation itself lives in styles/index.css (.skeleton). Used directly for
 * one-off placeholders, and composed into the patterns below for whole
 * screens (tables, card grids, detail pages).
 */
export function Skeleton({ className }) {
  return <div className={clsx('skeleton rounded-md', className)} aria-hidden="true" />;
}

/** A single placeholder line — the most common shape, so it gets a shorthand. */
export function SkeletonLine({ className = 'h-4 w-full' }) {
  return <Skeleton className={className} />;
}

/**
 * Desktop table body rows — drop straight into an existing <table>/<tbody>
 * so the real header stays visible while the rows are loading. `cols`
 * accepts either a count (even-width columns) or an array of width
 * classes for a closer match to the real column widths.
 */
export function SkeletonTableRows({ rows = 5, cols = 4 }) {
  const widths = Array.isArray(cols) ? cols : Array.from({ length: cols }).map(() => 'w-full max-w-[140px]');
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className="border-b border-border last:border-0">
          {widths.map((w, c) => (
            <td key={c} className="py-3 pr-4">
              <Skeleton className={clsx('h-4', w)} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/** Mobile card-list placeholder — mirrors the MobileCard rows these replace while loading. */
export function SkeletonMobileCards({ rows = 3, lines = 2 }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="rounded-xl border border-border bg-background-elevated/40 p-4 space-y-2.5">
          <div className="flex items-center justify-between gap-3">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          {Array.from({ length: lines }).map((_, l) => (
            <Skeleton key={l} className="h-3 w-full" />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Full responsive table placeholder — real header cells (so the columns
 * are recognizable immediately) with shimmering body rows, plus the
 * matching mobile card-list placeholder. `columns` takes the same header
 * labels the real table renders, in order.
 */
export function SkeletonTable({ columns = [], rows = 5 }) {
  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-foreground-subtle border-b border-border">
              {columns.map((label, i) => (
                <th key={i} className="py-2 pr-4 font-medium">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <SkeletonTableRows rows={rows} cols={columns.length || 4} />
          </tbody>
        </table>
      </div>
      <div className="md:hidden">
        <SkeletonMobileCards rows={Math.min(rows, 4)} />
      </div>
    </>
  );
}

/** Card-grid placeholder — plans catalog, investor investment cards, etc. */
export function SkeletonCardGrid({ count = 6, className = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' }) {
  return (
    <div className={className}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="glass-card p-4 space-y-3">
          <Skeleton className="h-28 w-full rounded-lg" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ))}
    </div>
  );
}

/**
 * Just the header (title + subtitle + badge) and label/value field grid,
 * with no outer card — for dropping inside a container that already
 * provides its own (a Modal, an existing <Card>), so it doesn't double up
 * the border/background.
 */
export function SkeletonFields({ fields = 4, withHeader = true }) {
  return (
    <div className="space-y-5">
      {withHeader && (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-3 w-28" />
          </div>
          <Skeleton className="h-6 w-24 rounded-full" />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: fields }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-4 w-24" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Full detail-page placeholder — SkeletonFields wrapped in its own card,
 * matching the shape of InvestorDetail, InvestmentDetail, KycReviewDetail,
 * etc. when the page itself doesn't already render a <Card>.
 */
export function SkeletonDetailCard({ fields = 4 }) {
  return (
    <div className="glass-card p-4 sm:p-6">
      <SkeletonFields fields={fields} />
    </div>
  );
}

export default Skeleton;
